/**
 * GoalRoadmap v3
 *
 * One node per month group. Goals starting in the same month are stacked
 * as cards at that node.  The path zigzags between a top lane and a bottom
 * lane via S-curve bezier connectors.  A flag marker bookends the start and
 * a trophy marker bookends the end.
 */
import React, { useMemo, useRef, useState, useCallback, useEffect } from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

export type RoadmapGoal = {
  id: string;
  title: string;
  description?: string;
  completed?: boolean;
  startMonth?: number;
  startYear?: number;
  endMonth?: number;
  endYear?: number;
};

type MonthGroup = {
  key: string;
  month: number;
  year: number;
  goals: RoadmapGoal[];
  stepIndex: number;   // 1-based
  lane: "top" | "bottom";
  x: number;
  y: number;
};

// ─── Layout constants ─────────────────────────────────────────────────────────

const NODE_R      = 40;   // month-node circle radius (px)
const MARKER_R    = 28;   // flag / trophy circle radius (px)
const STEP_W      = 286;  // x-distance between consecutive month nodes
const PAD_LEFT    = 100;  // canvas left padding (room for flag marker)
const PAD_RIGHT   = 90;   // canvas right padding (room for trophy marker)
const TOP_Y       = 155;  // y-centre for top lane
const BOT_Y       = 370;  // y-centre for bottom lane
const CANVAS_H    = 540;  // fixed canvas height
const CARD_W      = 200;  // goal card width
const CARD_H_EST  = 86;   // estimated height per card (inc. gap) for upward stacking
const CARD_GAP    = 8;
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function chronIdx(m: number | undefined, y: number | undefined) {
  return (y ?? 0) * 12 + ((m ?? 1) - 1);
}

function mkKey(m: number, y: number) {
  return `${y}-${String(m).padStart(2, "0")}`;
}

// ─── Group goals by startMonth/startYear ─────────────────────────────────────

function buildGroups(goals: RoadmapGoal[]): MonthGroup[] {
  if (!goals.length) return [];

  const sorted = [...goals].sort(
    (a, b) =>
      chronIdx(a.startMonth ?? a.endMonth, a.startYear ?? a.endYear) -
      chronIdx(b.startMonth ?? b.endMonth, b.startYear ?? b.endYear),
  );

  // Preserve insertion order
  const map = new Map<string, RoadmapGoal[]>();
  for (const g of sorted) {
    const m = g.startMonth ?? g.endMonth ?? 1;
    const y = g.startYear  ?? g.endYear  ?? 0;
    const k = mkKey(m, y);
    if (!map.has(k)) map.set(k, []);
    map.get(k)!.push(g);
  }

  let stepIndex = 1;
  let lane: "top" | "bottom" = "top";
  let x = PAD_LEFT + NODE_R;

  const groups: MonthGroup[] = [];
  map.forEach((grpGoals, key) => {
    const [yearStr, monthStr] = key.split("-");
    groups.push({
      key,
      month: Number(monthStr),
      year:  Number(yearStr),
      goals: grpGoals,
      stepIndex: stepIndex++,
      lane,
      x,
      y: lane === "top" ? TOP_Y : BOT_Y,
    });
    lane = lane === "top" ? "bottom" : "top";
    x += STEP_W;
  });

  return groups;
}

// ─── SVG path between two nodes ──────────────────────────────────────────────

function sCurve(x1: number, y1: number, x2: number, y2: number): string {
  const ctrl = (x2 - x1) * 0.52;
  return `M ${x1} ${y1} C ${x1 + ctrl} ${y1} ${x2 - ctrl} ${y2} ${x2} ${y2}`;
}

// ─── Icons (inline SVG) ───────────────────────────────────────────────────────

const FlagIcon = ({ color }: { color: string }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
    <line x1="4" y1="22" x2="4" y2="15" />
  </svg>
);

const TrophyIcon = ({ color }: { color: string }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2z" />
  </svg>
);

// ─── Start / End bookmark circles ────────────────────────────────────────────

type BookmarkProps = {
  x: number;
  y: number;
  type: "start" | "end";
  dreamColor: string;
};

const Bookmark: React.FC<BookmarkProps> = ({ x, y, type, dreamColor }) => {
  const isStart    = type === "start";
  const bg         = isStart ? "#eff6ff" : "#eff6ff";
  const border     = isStart ? "#0088FF" : "#0088FF";
  const iconColor  = border;
  const label      = isStart ? "Start" : "Dream";

  return (
    <React.Fragment>
      {/* Glow ring */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: x - MARKER_R - 8,
          top:  y - MARKER_R - 8,
          width:  (MARKER_R + 8) * 2,
          height: (MARKER_R + 8) * 2,
          borderRadius: "50%",
          background: `${border}18`,
          zIndex: 2,
          pointerEvents: "none",
        }}
      />
      {/* Circle */}
      <div
        aria-label={isStart ? "Start of roadmap" : "End of roadmap"}
        style={{
          position: "absolute",
          left: x - MARKER_R,
          top:  y - MARKER_R,
          width:  MARKER_R * 2,
          height: MARKER_R * 2,
          borderRadius: "50%",
          background: bg,
          border: `2.5px solid ${border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: `0 3px 12px ${border}28`,
          zIndex: 4,
        }}
      >
        {isStart ? <FlagIcon color={iconColor} /> : <TrophyIcon color={iconColor} />}
      </div>
      {/* Label */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: x - 24,
          top:  y + MARKER_R + 5,
          width: 48,
          textAlign: "center",
          fontSize: 9,
          fontWeight: 800,
          color: border,
          letterSpacing: 0.5,
          zIndex: 4,
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
    </React.Fragment>
  );
};

// ─── Month node circle ────────────────────────────────────────────────────────

const MonthNode: React.FC<{ group: MonthGroup; dreamColor: string }> = ({ group, dreamColor }) => {
  const { x, y, lane, stepIndex, month, year, goals } = group;
  const allDone    = goals.every((g) => !!g.completed);
  const monthLabel = MONTHS[(month - 1) % 12] ?? "???";
  const stepTop    = lane === "top" ? y - NODE_R - 20 : y + NODE_R + 6;

  return (
    <React.Fragment>
      {/* Glow ring */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: x - NODE_R - 10,
          top:  y - NODE_R - 10,
          width:  (NODE_R + 10) * 2,
          height: (NODE_R + 10) * 2,
          borderRadius: "50%",
          background: "rgba(0,0,0,0.04)",
          zIndex: 2,
          pointerEvents: "none",
        }}
      />
      {/* Circle */}
      <div
        aria-label={`Step ${stepIndex}: ${monthLabel} ${year}`}
        style={{
          position: "absolute",
          left: x - NODE_R,
          top:  y - NODE_R,
          width:  NODE_R * 2,
          height: NODE_R * 2,
          borderRadius: "50%",
          background: allDone ? dreamColor : "#fff",
          border: `3px solid ${dreamColor}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          zIndex: 4,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 800, color: allDone ? "#fff" : "#0b1b3a", lineHeight: 1 }}>
          {monthLabel}
        </span>
        <span style={{ fontSize: 10, fontWeight: 600, color: allDone ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.38)", lineHeight: 1, marginTop: 3 }}>
          {year || "—"}
        </span>
      </div>
      {/* Step label */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: x - 22,
          top: stepTop,
          width: 44,
          textAlign: "center",
          fontSize: 10,
          fontWeight: 800,
          color: "#64748b",
          letterSpacing: 0.4,
          zIndex: 4,
        }}
      >
        {String(stepIndex).padStart(2, "0")}
      </div>
    </React.Fragment>
  );
};

// ─── Stacked goal cards ───────────────────────────────────────────────────────

const GoalCardStack: React.FC<{ group: MonthGroup; dreamColor: string }> = ({ group, dreamColor }) => {
  const { x, y, lane, goals } = group;
  const n = goals.length;

  // Top lane → cards go below the circle; bottom lane → cards go above
  const stackTop = lane === "top"
    ? y + NODE_R + 14
    : y - NODE_R - 14 - n * (CARD_H_EST + CARD_GAP);

  return (
    <div
      style={{
        position: "absolute",
        left: x - CARD_W / 2,
        top: stackTop,
        width: CARD_W,
        display: "flex",
        flexDirection: "column",
        gap: CARD_GAP,
        zIndex: 2,
      }}
    >
      {goals.map((goal) => {
        const isDone = !!goal.completed;
        return (
          <div
            key={goal.id}
            style={{
              background: isDone ? "#f0fdf4" : "#fff",
              border: isDone ? "1px solid #86efac" : "1px solid rgba(0,0,0,0.08)",
              borderLeft: `4px solid ${isDone ? "#22c55e" : `${dreamColor}55`}`,
              borderRadius: 10,
              padding: "9px 11px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            }}
          >
            <div style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 5,
              marginBottom: goal.description ? 3 : 0,
            }}>
              {isDone && (
                <span style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  background: "#22c55e",
                  flexShrink: 0,
                  marginTop: 1,
                }}>
                  <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
                    <polyline points="1.5,5 4,7.5 8.5,2" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              )}
              <span style={{
                fontSize: 12,
                fontWeight: 700,
                color: isDone ? "#15803d" : "#0b1b3a",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                textOverflow: "ellipsis",
                lineHeight: 1.35,
              }}>
                {goal.title}
              </span>
            </div>
            {goal.description ? (
              <div style={{
                fontSize: 11,
                color: "rgba(0,0,0,0.42)",
                marginTop: 4,
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                textOverflow: "ellipsis",
                lineHeight: 1.3,
              }}>
                {goal.description}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export type GoalRoadmapProps = {
  goals: RoadmapGoal[];
  dreamColor?: string;
};

const MIN_ZOOM = 0.4;
const MAX_ZOOM = 2.0;
const ZOOM_STEP = 0.15;

export const GoalRoadmap: React.FC<GoalRoadmapProps> = ({
  goals,
  dreamColor = "#8B5CF6",
}) => {
  const groups = useMemo(() => buildGroups(goals ?? []), [goals]);

  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanMode, setIsPanMode] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef<{ mx: number; my: number; px: number; py: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 640);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const zoomIn  = useCallback(() => setZoom((z) => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2))), []);
  const zoomOut = useCallback(() => setZoom((z) => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2))), []);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (!isPanMode) return;
    e.preventDefault();
    dragStart.current = { mx: e.clientX, my: e.clientY, px: pan.x, py: pan.y };
    setIsDragging(true);
  }, [isPanMode, pan]);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging || !dragStart.current) return;
    const dx = e.clientX - dragStart.current.mx;
    const dy = e.clientY - dragStart.current.my;
    setPan({ x: dragStart.current.px + dx, y: dragStart.current.py + dy });
  }, [isDragging]);

  const onMouseUp = useCallback(() => {
    dragStart.current = null;
    setIsDragging(false);
  }, []);

  if (!groups.length) {
    return (
      <div
        className="flex h-40 items-center justify-center rounded-xl bg-slate-50 text-sm text-slate-400"
        role="status"
      >
        No goals to display on the roadmap yet.
      </div>
    );
  }

  /* ── Mobile vertical layout ────────────────────────────────────────────── */
  const MOB_NODE_R   = 30;
  const MOB_MARKER_R = 22;

  if (isMobile) {
    return (
      <div
        className="rounded-xl p-4"
        style={{ background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)" }}
        role="region"
        aria-label="Goal roadmap"
      >
        <div style={{ display: "flex", flexDirection: "column" }}>

          {/* Start bookmark */}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: MOB_MARKER_R * 2, height: MOB_MARKER_R * 2, borderRadius: "50%",
              background: "#eff6ff", border: "2.5px solid #0088FF",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              boxShadow: "0 3px 10px #0088FF28",
            }}>
              <FlagIcon color="#0088FF" />
            </div>
            <span style={{ fontSize: 11, fontWeight: 800, color: "#0088FF", textTransform: "uppercase", letterSpacing: 0.5 }}>Start</span>
          </div>

          {groups.map((group, _i) => {
            const allDone    = group.goals.every((g) => !!g.completed);
            const monthLabel = MONTHS[(group.month - 1) % 12] ?? "???";
            return (
              <React.Fragment key={group.key}>
                {/* Connector line */}
                <div style={{ display: "flex" }}>
                  <div style={{ width: MOB_MARKER_R * 2, display: "flex", justifyContent: "center" }}>
                    <div style={{ width: 2, height: 20, background: "#cbd5e1" }} />
                  </div>
                </div>

                {/* Step number */}
                <div style={{ display: "flex", marginBottom: 2 }}>
                  <div style={{ width: MOB_MARKER_R * 2, display: "flex", justifyContent: "center" }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: "#64748b", letterSpacing: 0.4 }}>
                      {String(group.stepIndex).padStart(2, "0")}
                    </span>
                  </div>
                </div>

                {/* Node + cards row */}
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  {/* Node circle */}
                  <div style={{
                    width: MOB_NODE_R * 2, height: MOB_NODE_R * 2, borderRadius: "50%",
                    background: allDone ? dreamColor : "#fff",
                    border: `3px solid ${dreamColor}`,
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    flexShrink: 0, boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    marginLeft: (MOB_MARKER_R - MOB_NODE_R),
                  }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: allDone ? "#fff" : "#0b1b3a", lineHeight: 1 }}>{monthLabel}</span>
                    <span style={{ fontSize: 10, fontWeight: 600, color: allDone ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.38)", lineHeight: 1, marginTop: 3 }}>{group.year || "—"}</span>
                  </div>

                  {/* Goal cards */}
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8, paddingTop: 4 }}>
                    {group.goals.map((goal) => {
                      const isDone = !!goal.completed;
                      return (
                        <div
                          key={goal.id}
                          style={{
                            background: isDone ? "#f0fdf4" : "#fff",
                            border: isDone ? "1px solid #86efac" : "1px solid rgba(0,0,0,0.08)",
                            borderLeft: `4px solid ${isDone ? "#22c55e" : `${dreamColor}55`}`,
                            borderRadius: 10,
                            padding: "9px 11px",
                            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 5, marginBottom: goal.description ? 3 : 0 }}>
                            {isDone && (
                              <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 14, height: 14, borderRadius: "50%", background: "#22c55e", flexShrink: 0, marginTop: 1 }}>
                                <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
                                  <polyline points="1.5,5 4,7.5 8.5,2" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </span>
                            )}
                            <span style={{ fontSize: 12, fontWeight: 700, color: isDone ? "#15803d" : "#0b1b3a", lineHeight: 1.35 }}>{goal.title}</span>
                          </div>
                          {goal.description && (
                            <div style={{ fontSize: 11, color: "rgba(0,0,0,0.42)", marginTop: 4, lineHeight: 1.3 }}>
                              {goal.description}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          {/* Final connector */}
          <div style={{ display: "flex" }}>
            <div style={{ width: MOB_MARKER_R * 2, display: "flex", justifyContent: "center" }}>
              <div style={{ width: 2, height: 20, background: "#0088FF80" }} />
            </div>
          </div>

          {/* End bookmark */}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: MOB_MARKER_R * 2, height: MOB_MARKER_R * 2, borderRadius: "50%",
              background: "#eff6ff", border: "2.5px solid #0088FF",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              boxShadow: "0 3px 10px #0088FF28",
            }}>
              <TrophyIcon color="#0088FF" />
            </div>
            <span style={{ fontSize: 11, fontWeight: 800, color: "#0088FF", textTransform: "uppercase", letterSpacing: 0.5 }}>Dream</span>
          </div>
        </div>
      </div>
    );
  }

  const first      = groups[0];
  const last       = groups[groups.length - 1];

  // Start flag: slightly left of first node, same lane (top)
  const startX = PAD_LEFT / 2;
  const startY = TOP_Y;

  // End trophy: slightly right of last node, same lane as last
  const endX = last.x + STEP_W / 2;
  const endY = last.y;

  const canvasW = endX + PAD_RIGHT;

  // Path from start flag to first node
  const startPath = sCurve(startX, startY, first.x, first.y);
  // Path from last node to trophy
  const endPath   = sCurve(last.x, last.y, endX, endY);

  const ctrlBtnStyle = (active?: boolean): React.CSSProperties => ({
    width: 32,
    height: 32,
    borderRadius: 8,
    border: active ? "1.5px solid #0088FF" : "1.5px solid rgba(0,0,0,0.12)",
    background: active ? "#eff6ff" : "#fff",
    color: active ? "#0088FF" : "#64748b",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    flexShrink: 0,
    transition: "all 0.15s",
  });

  return (
    <div
      ref={containerRef}
      className="rounded-xl p-3"
      style={{
        background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
        overflow: "hidden",
        position: "relative",
        userSelect: isDragging ? "none" : "auto",
      }}
      role="region"
      aria-label="Goal roadmap"
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
    >
      {/* ── Zoom / Pan controls ─────────────────────────────────────── */}
      <div
        style={{
          position: "absolute",
          top: 12,
          right: 12,
          display: "flex",
          gap: 6,
          zIndex: 20,
        }}
      >
        <button type="button" onClick={zoomIn}  style={ctrlBtnStyle()} aria-label="Zoom in">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.5"/>
            <line x1="4" y1="6" x2="8" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="6" y1="4" x2="6" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="9.5" y1="9.5" x2="13" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </button>
        <button type="button" onClick={zoomOut} style={ctrlBtnStyle()} aria-label="Zoom out">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.5"/>
            <line x1="4" y1="6" x2="8" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="9.5" y1="9.5" x2="13" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </button>
        <button
          type="button"
          onClick={() => { setIsPanMode((v) => !v); setIsDragging(false); }}
          style={ctrlBtnStyle(isPanMode)}
          aria-label="Pan mode"
          aria-pressed={isPanMode}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0" />
            <path d="M14 10V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v2" />
            <path d="M10 10.5V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v8" />
            <path d="M18 11a2 2 0 1 1 4 0v3a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
          </svg>
        </button>
      </div>

      {/* ── Scrollable + zoomable canvas ────────────────────────────── */}
      <div
        style={{
          overflowX: isPanMode ? "hidden" : "auto",
          overflowY: isPanMode ? "hidden" : "visible",
          overscrollBehaviorX: "none",
          cursor: isPanMode ? (isDragging ? "grabbing" : "grab") : "default",
        }}
        onMouseDown={onMouseDown}
      >
      <div style={{
        transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
        transformOrigin: "top left",
        transition: isDragging ? "none" : "transform 0.15s ease",
      }}>
      <div style={{ position: "relative", width: canvasW, height: CANVAS_H, minWidth: canvasW }}>

        {/* ── SVG track ──────────────────────────────────────────────── */}
        <svg
          aria-hidden="true"
          style={{ position: "absolute", top: 0, left: 0, width: canvasW, height: CANVAS_H, overflow: "visible", zIndex: 1 }}
        >
          {/* Start → first node */}
          <path d={startPath} fill="none" stroke="#0088FF28" strokeWidth={10} strokeLinecap="round" />
          <path d={startPath} fill="none" stroke="#0088FF80" strokeWidth={2.5} strokeLinecap="round" />

          {/* Node-to-node connectors */}
          {groups.slice(0, -1).map((g, i) => {
            const next = groups[i + 1];
            const d    = sCurve(g.x, g.y, next.x, next.y);
            return (
              <React.Fragment key={`conn-${g.key}`}>
                <path d={d} fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth={12} strokeLinecap="round" />
                <path d={d} fill="none" stroke="#cbd5e1" strokeWidth={2.5} strokeLinecap="round" />
              </React.Fragment>
            );
          })}

          {/* Last node → trophy */}
          <path d={endPath} fill="none" stroke="#0088FF28" strokeWidth={10} strokeLinecap="round" />
          <path d={endPath} fill="none" stroke="#0088FF80" strokeWidth={2.5} strokeLinecap="round" />
        </svg>

        {/* ── Goal card stacks ────────────────────────────────────────── */}
        {groups.map((g) => (
          <GoalCardStack key={`stack-${g.key}`} group={g} dreamColor={dreamColor} />
        ))}

        {/* ── Month node circles ──────────────────────────────────────── */}
        {groups.map((g) => (
          <MonthNode key={`node-${g.key}`} group={g} dreamColor={dreamColor} />
        ))}

        {/* ── Start flag ─────────────────────────────────────────────── */}
        <Bookmark x={startX} y={startY} type="start" dreamColor={dreamColor} />

        {/* ── End trophy ─────────────────────────────────────────────── */}
        <Bookmark x={endX} y={endY} type="end" dreamColor={dreamColor} />

      </div>
      </div>
      </div>
    </div>
  );
};

export default GoalRoadmap;
